<template>
    <div
        id="deal-statuses-by-type"
        class="space-y-4"
        :class="types.length === 0 ? 'hidden' : 'block'"
    >
        <h5>{{ _mft('crm:dealStatusSettings.title') }}</h5>
        <p class="text-sm text-gray-500">{{ _mft('crm:dealStatusSettings.description') }}</p>

        <card-sub
            v-for="dealType in types"
            :key="dealType.id"
            class="p-4 rounded-lg border border-gray-200"
        >
            <div class="flex items-center justify-between">
                <h6 class="font-semibold text-gray-900">{{ dealType.type_name }}</h6>
                <div class="flex items-center space-x-2">
                    <span class="text-xs text-gray-500">
                        {{ _mft('crm:dealStatusSettings.statusesCount', { count: (dealType.statuses || []).length }) }}
                    </span>
                    <button-secondary
                        size="sm"
                        @click="toggleSectionVisible(dealType.id)"
                    >
                        <i class="fa-regular" :class="expandedSections.includes(dealType.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </button-secondary>
                </div>
            </div>

            <div v-if="expandedSections.includes(dealType.id)">
                <div class="mt-3 space-y-2">
                    <div class="grid-cols-12 gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500 hidden lg:grid">
                        <div class="col-span-1">{{ _mft('crm:dealStatusSettings.order') }}</div>
                        <div class="col-span-3">{{ _mft('shared:name') }}</div>
                        <div class="col-span-2">{{ _mft('crm:dealStatusSettings.likelihoodPercent') }}</div>
                        <div class="col-span-4">{{ _mft('crm:dealStatusSettings.closedWon') }}</div>
                        <div class="col-span-2 text-right">{{ _mft('crm:dealStatusSettings.actions') }}</div>
                    </div>

                    <draggable
                        v-model="dealType.statuses"
                        item-key="id"
                        handle=".status-drag-handle"
                        ghost-class="status-row-ghost"
                        class="space-y-2"
                        @end="reorderStatuses(dealType)"
                    >
                        <template #item="{ element: status }">
                            <div class="lg:grid grid-cols-12 gap-x-2 items-center p-2 rounded bg-gray-50 space-y-2 lg:space-y-0 relative">
                                <div class="col-span-1">
                                    <button
                                        type="button"
                                        class="status-drag-handle inline-flex items-center justify-center w-8 h-8 text-gray-500 hover:text-myflowMagenta cursor-move rounded absolute top-2.5 -left-5 lg:static"
                                        :title="_mft('crm:dealStatusSettings.dragToReorder')"
                                    >
                                        <i class="fas fa-grip-vertical"></i>
                                    </button>
                                </div>
                                <div class="col-span-3">
                                    <input-label label="shared:name" class="lg:hidden"/>
                                    <input-text
                                        type="text"
                                        v-model:content="status.name"
                                        class="!mb-0"
                                    />
                                </div>
                                <div class="col-span-2">
                                    <input-label label="crm:dealStatusSettings.likelihoodPercent" class="lg:hidden"/>
                                    <input-text
                                        type="number"
                                        min="0"
                                        max="100"
                                        v-model:content="status.likelihood"
                                        class="!mb-0"
                                    />
                                </div>
                                <div class="col-span-4 text-xs text-gray-600 pt-1">
                                    <input-label label="crm:dealStatusSettings.closedWon" class="lg:hidden mb-1"/>
                                    <label class="inline-flex items-center gap-1 mr-4">
                                        <input type="checkbox" v-model="status.is_closed">
                                        {{ _mft('crm:dealStatusSettings.closed') }}
                                    </label>

                                    <label class="inline-flex items-center gap-1">
                                        <input type="checkbox" v-model="status.is_won" @change="markWonAsClosed(status)">
                                        {{ _mft('crm:dealStatusSettings.won') }}
                                    </label>
                                </div>
                                <div class="col-span-2 flex justify-end gap-1">
                                    <button-success
                                        v-if="isStatusDirty(status)"
                                        size="xs"
                                        type="button"
                                        class="text-xs rounded border border-emerald-200 text-emerald-700 bg-emerald-50"
                                        @click="updateStatus(status)"
                                    >
                                        {{ _mft('shared:action.save') }}
                                    </button-success>
                                    <button-default
                                        type="button"
                                        size="xs"
                                        :style-type="status.deals_count > 0 ? 'disabled' : 'danger'"
                                        class="text-xs rounded border"
                                        :class="status.deals_count > 0
                                            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'border-rose-200 text-rose-700 bg-rose-50'"
                                        :disabled="status.deals_count > 0"
                                        @click="deleteStatus(status)"
                                    >
                                        {{ _mft('shared:action.delete') }}
                                    </button-default>
                                </div>
                                <div
                                    class="col-span-12 text-xs text-gray-400 text-right"
                                    v-if="status.deals_count > 0"
                                >
                                    {{ _mft('crm:dealStatusSettings.usedByDealsCannotDelete', { count: status.deals_count }) }}
                                </div>
                            </div>
                        </template>
                    </draggable>
                </div>

                <div class="mt-4 p-3 grid grid-cols-12 gap-2 items-center border-t">
                    <div class="col-span-4">
                        <input
                            type="text"
                            v-model="draftForType(dealType.id).name"
                            :placeholder="_mft('crm:dealStatusSettings.statusNamePlaceholder')"
                            class="block w-full p-2 border rounded-md text-sm"
                        >
                    </div>
                    <div class="col-span-2">
                        <input
                            type="number"
                            min="0"
                            max="100"
                            v-model.number="draftForType(dealType.id).likelihood"
                            class="block w-full p-2 border rounded-md text-sm"
                        >
                    </div>
                    <div class="col-span-2 text-xs text-gray-700">
                        <label class="inline-flex items-center gap-1">
                            <input type="checkbox" v-model="draftForType(dealType.id).is_closed">
                            {{ _mft('crm:dealStatusSettings.closed') }}
                        </label>
                    </div>
                    <div class="col-span-2 text-xs text-gray-700">
                        <label class="inline-flex items-center gap-1">
                            <input type="checkbox" v-model="draftForType(dealType.id).is_won" @change="markWonAsClosed(draftForType(dealType.id))">
                            {{ _mft('crm:dealStatusSettings.won') }}
                        </label>
                    </div>
                    <div class="col-span-2 flex justify-end">
                        <button
                            type="button"
                            class="myflow-basic-button"
                            @click="createStatus(dealType.id)"
                        >
                            {{ _mft('shared:action.add') }}
                        </button>
                    </div>
                </div>
            </div>
        </card-sub>

        <p v-if="message" class="text-xs" :class="messageType === 'error' ? 'text-rose-700' : 'text-emerald-700'">
            {{ message }}
        </p>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

export default {
    components: {
        draggable,
    },
    props: {
        dealTypes: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            types: [],
            drafts: {},
            statusOriginals: {},
            message: '',
            messageType: 'success',
            expandedSections: [],
        };
    },
    methods: {
        hydrateTypes() {
            this.types = JSON.parse(JSON.stringify(this.dealTypes || []));
        },
        draftForType(typeId) {
            if (!this.drafts[typeId]) {
                this.drafts[typeId] = {
                    name: '',
                    likelihood: 50,
                    is_closed: false,
                    is_won: false,
                };
            }

            return this.drafts[typeId];
        },
        markWonAsClosed(entity) {
            if (entity.is_won) {
                entity.is_closed = true;
                entity.likelihood = 100;
            }
        },
        async refreshStatuses() {
            const response = await axios.get(this.route('crm.deal.statuses'));
            const statuses = response.data.statuses || [];

            const grouped = statuses.reduce((carry, status) => {
                if (!carry[status.lead_deal_type_id]) {
                    carry[status.lead_deal_type_id] = [];
                }

                carry[status.lead_deal_type_id].push(status);
                return carry;
            }, {});

            this.types = this.types.map((type) => {
                return {
                    ...type,
                    statuses: (grouped[type.id] || []).sort((a, b) => {
                        if (a.position === b.position) {
                            return a.id - b.id;
                        }

                        return a.position - b.position;
                    }),
                };
            });

            this.captureStatusOriginals();
        },
        captureStatusOriginals() {
            const originals = {};
            this.types.forEach((type) => {
                (type.statuses || []).forEach((status) => {
                    originals[status.id] = this.snapshotStatus(status);
                });
            });

            this.statusOriginals = originals;
        },
        snapshotStatus(status) {
            return {
                name: status.name,
                likelihood: Number(status.likelihood),
                is_closed: !!status.is_closed,
                is_won: !!status.is_won,
            };
        },
        isStatusDirty(status) {
            const original = this.statusOriginals[status.id];
            if (!original) {
                return false;
            }

            const current = this.snapshotStatus(status);

            return (
                original.name !== current.name ||
                original.likelihood !== current.likelihood ||
                original.is_closed !== current.is_closed ||
                original.is_won !== current.is_won
            );
        },
        async reorderStatuses(dealType) {
            const statusIds = (dealType.statuses || []).map((status) => status.id);

            try {
                await axios.post(this.route('crm.settingsDealStatus.reorder'), {
                    lead_deal_type_id: dealType.id,
                    status_ids: statusIds,
                });

                await this.refreshStatuses();
                this.messageType = 'success';
                this.message = _mft('crm:dealStatusSettings.statusOrderUpdated');
            } catch (error) {
                await this.refreshStatuses();
                this.messageType = 'error';
                this.message = error?.response?.data?.message || _mft('crm:dealStatusSettings.couldNotReorder');
            }
        },
        async createStatus(dealTypeId) {
            const draft = this.draftForType(dealTypeId);

            if (!draft.name || draft.name.trim().length === 0) {
                this.messageType = 'error';
                this.message = _mft('crm:dealStatusSettings.statusNameRequired');
                return;
            }

            try {
                await axios.post(this.route('crm.settingsDealStatus.store'), {
                    lead_deal_type_id: dealTypeId,
                    name: draft.name,
                    likelihood: draft.likelihood,
                    is_closed: !!draft.is_closed,
                    is_won: !!draft.is_won,
                });

                this.drafts[dealTypeId] = {
                    name: '',
                    likelihood: 50,
                    is_closed: false,
                    is_won: false,
                };

                await this.refreshStatuses();
                this.messageType = 'success';
                this.message = _mft('crm:dealStatusSettings.statusAdded');
            } catch (error) {
                this.messageType = 'error';
                this.message = error?.response?.data?.message || _mft('crm:dealStatusSettings.couldNotAdd');
            }
        },
        async updateStatus(status) {
            if (!this.isStatusDirty(status)) {
                return;
            }

            try {
                await axios.put(this.route('crm.settingsDealStatus.update', status.id), {
                    name: status.name,
                    likelihood: status.likelihood,
                    is_closed: !!status.is_closed,
                    is_won: !!status.is_won,
                    position: status.position,
                });

                await this.refreshStatuses();
                this.messageType = 'success';
                this.message = _mft('crm:dealStatusSettings.statusUpdated');
            } catch (error) {
                this.messageType = 'error';
                this.message = error?.response?.data?.message || _mft('crm:dealStatusSettings.couldNotUpdate');
            }
        },
        async deleteStatus(status) {
            if (status.deals_count > 0) {
                return;
            }

            if (!window.confirm(_mft('crm:dealStatusSettings.archiveConfirm'))) {
                return;
            }

            try {
                await axios.delete(this.route('crm.settingsDealStatus.delete', status.id));
                await this.refreshStatuses();
                this.messageType = 'success';
                this.message = _mft('crm:dealStatusSettings.statusArchived');
            } catch (error) {
                this.messageType = 'error';
                this.message = error?.response?.data?.message || _mft('crm:dealStatusSettings.couldNotArchive');
            }
        },
        toggleSectionVisible(dealTypeId) {
            if (this.expandedSections.includes(dealTypeId)) {
                this.expandedSections = this.expandedSections.filter((id) => id !== dealTypeId);
            } else {
                this.expandedSections.push(dealTypeId);
            }
        },
    },
    async mounted() {
        this.hydrateTypes();
        await this.refreshStatuses();
    },
};
</script>

<style scoped>
.status-row-ghost {
    opacity: 0.45;
}
</style>
